import { z } from "zod";

import { PAID, WALLETS, isValidPhone, normalizePhone } from "@pharmacy/domain";
import { moneyPositive, moneyText } from "../money";

/**
 * Deyn: customers who owe the shop, what they owe and for how long, and the
 * payments that bring it down.
 *
 * Each branch keeps its own customers. A customer is found by their phone
 * number: selling on credit to a new number adds them. There are no credit
 * limits, as the owner decided.
 */

const phone = z
  .string()
  .trim()
  .max(30)
  .refine((v) => isValidPhone(v), "That is not a valid phone number")
  .transform((v) => normalizePhone(v)!);

/** What is still owed, by how long ago it was taken. */
export const debtAges = z.object({
  days30: moneyText,
  days60: moneyText,
  days90: moneyText,
  older: moneyText,
});

/**
 * One line of a customer's account. `amount` is signed: a credit sale
 * adds to what is owed; a payment, or a credit sale cancelled, takes away.
 */
export const customerEntry = z.object({
  id: z.string().uuid(),
  /**
   * `credit` is credit added on the Credits page with no sale — an old debt
   * from the notebook (2026-10-18). Older phones read it as a sale with no
   * receipt, which is close enough.
   */
  kind: z.enum(["sale", "payment", "cancelled", "credit"]),
  amount: moneyText,
  /** When it happened: the sale's own moment, or when the payment was taken. */
  occurredAt: z.string(),
  /** The sale's receipt, for a sale or a cancelled one. */
  saleId: z.string().uuid().nullable(),
  receiptNo: z.string().nullable(),
  /** The wallet, for a payment. */
  method: z.string().nullable(),
  note: z.string().nullable(),
  /**
   * On a change to a Credits-page credit, as the phone's copy receives it:
   * the credit it changes. The account page folds it into that credit
   * (foldCredits), so it is never shown as a line of its own.
   */
  creditId: z.string().uuid().nullable().optional(),
  /** A Credits-page credit changed since: what it was first written for. */
  originalAmount: moneyText.optional(),
  /** A Credits-page credit cancelled: it comes to nothing now. */
  cancelled: z.boolean().optional(),
});

export const customersQuery = z.object({
  branchId: z.string().uuid(),
  q: z.string().trim().max(120).optional(),
});

export const customerSummary = z.object({
  id: z.string().uuid(),
  name: z.string(),
  phone: z.string().nullable(),
  balance: moneyText,
});

export const customersResponse = z.object({
  /** Everything owed to this branch, and how old it is. */
  owed: moneyText,
  ages: debtAges,
  /** Those who owe, biggest first — or, when searching, everyone who matches. */
  customers: z.array(customerSummary),
});

export const customerResponse = customerSummary.extend({
  ages: debtAges,
  /** Newest first. */
  entries: z.array(customerEntry),
});

/**
 * A payment towards what a customer owes, by anybody who can sell. Made on
 * the phone, with its own id, so it can wait in the queue with no internet
 * and be sent twice without being taken twice.
 *
 * The customer is named by phone number — a customer added offline by a
 * credit sale has no id the server knows yet.
 */
export const recordPaymentRequest = z.object({
  id: z.string().uuid(),
  branchId: z.string().uuid(),
  customerPhone: phone,
  amount: moneyPositive("Enter an amount"),
  /** `paid` from this version on; a wallet's name from phones still on an older one. */
  method: z.enum([...WALLETS, PAID]),
  note: z.string().trim().max(200).nullable().optional(),
  /** When it was taken, by the phone's corrected clock. */
  paidAt: z
    .string()
    .max(40)
    .refine((v) => !Number.isNaN(Date.parse(v)), "Not a time")
    .optional(),
});

/**
 * Credit added on the Credits page, not at a sale (the owner, 2026-10-18).
 * By anybody who can sell, with internet or without; sent twice, it is
 * recorded once.
 *
 * With no `saleId` it is an old debt: on the customer's account, not a sale —
 * no stock, and not in sales — and counted as money in only when paid back.
 * With one, it is a sale from the last 30 days that had no credit: that much
 * of it was not paid after all, and the sale itself changes from paid to on
 * credit, recorded as an edit of it.
 */
export const addCreditRequest = z.object({
  id: z.string().uuid(),
  branchId: z.string().uuid(),
  customerName: z.string().trim().min(2, "Enter the customer's name").max(120),
  customerPhone: phone,
  amount: moneyPositive("Enter an amount"),
  /** What it is for. */
  note: z.string().trim().max(200).nullable().optional(),
  /** The older sale it belongs to, when it does. */
  saleId: z.string().uuid().optional(),
  /** When it was given, by the phone's corrected clock. An old debt may be from long ago. */
  givenAt: z
    .string()
    .max(40)
    .refine((v) => !Number.isNaN(Date.parse(v)), "Not a time")
    .optional(),
});

/**
 * A credit added on the Credits page, corrected (the owner, 2026-10-19): its
 * amount and what it is for, as they should now be. Sent twice, done once —
 * `id` is the change's own, made on the phone.
 */
export const changeCreditRequest = z.object({
  id: z.string().uuid(),
  amount: moneyPositive("Enter an amount"),
  note: z.string().trim().max(200).nullable().optional(),
});

/** A credit added on the Credits page, taken off: it was a mistake. */
export const cancelCreditRequest = z.object({
  id: z.string().uuid(),
});

export const paymentResponse = z.object({
  customerId: z.string().uuid(),
  balance: moneyText,
});

export type DebtAges = z.infer<typeof debtAges>;
export type CustomerEntry = z.infer<typeof customerEntry>;
export type CustomersQuery = z.infer<typeof customersQuery>;
export type CustomerSummary = z.infer<typeof customerSummary>;
export type CustomersResponse = z.infer<typeof customersResponse>;
export type CustomerResponse = z.infer<typeof customerResponse>;
export type RecordPaymentRequest = z.infer<typeof recordPaymentRequest>;
export type PaymentResponse = z.infer<typeof paymentResponse>;
export type AddCreditRequest = z.infer<typeof addCreditRequest>;
export type ChangeCreditRequest = z.infer<typeof changeCreditRequest>;
export type CancelCreditRequest = z.infer<typeof cancelCreditRequest>;
